import type { WidgetFilterSettings } from '../lib/tradeCalculations'

/**
 * Dashboard widget registry.
 * Add new widgets here for extensibility.
 * Feed is always fixed on the right; only draggable widgets are configurable.
 */
export const DRAGGABLE_WIDGET_IDS = [
  'openPositions',
  'totalPnLPercent',
  'totalPnlOpen',
  'totalRiskExposure',
  'tradeList',
  'totalPnl',
  'totalTrades',
  'winRate',
  'profitFactor',
  'avgPnlPercent',
  'weightedPnlPercent',
  'expectancy',
  'averageWin',
  'averageLoss',
  'averageRR',
] as const

export const FILTERABLE_METRIC_WIDGET_IDS = [
  'totalPnl',
  'totalTrades',
  'winRate',
  'profitFactor',
  'avgPnlPercent',
  'weightedPnlPercent',
  'expectancy',
  'averageWin',
  'averageLoss',
  'averageRR',
] as const

export type FilterableMetricWidgetId = (typeof FILTERABLE_METRIC_WIDGET_IDS)[number]

/** Open-position widgets that support market + exchange filters only (no date/strategy). */
export const FILTERABLE_OPEN_WIDGET_IDS = ['totalRiskExposure'] as const
export type FilterableOpenWidgetId = (typeof FILTERABLE_OPEN_WIDGET_IDS)[number]

export const FIXED_WIDGET_IDS = ['feed'] as const

export const DASHBOARD_WIDGET_IDS = [
  ...DRAGGABLE_WIDGET_IDS,
  ...FIXED_WIDGET_IDS,
] as const

export type DraggableWidgetId = (typeof DRAGGABLE_WIDGET_IDS)[number]
/** Instance ID: baseId or baseId:uuid for multi-instance widgets */
export type WidgetInstanceId = string
export type FixedWidgetId = (typeof FIXED_WIDGET_IDS)[number]
export type DashboardWidgetId = (typeof DASHBOARD_WIDGET_IDS)[number]

export const DASHBOARD_GRID_COLS = 4
export const DASHBOARD_GRID_ROWS = 8

export interface DashboardWidgetConfig {
  id: DashboardWidgetId
  labelKey: string
  /** Width in grid units (1 to DASHBOARD_GRID_COLS) */
  width: number
  /** Height in grid units (1 to DASHBOARD_GRID_ROWS) */
  height: number
}

export const DASHBOARD_WIDGETS: Record<DashboardWidgetId, DashboardWidgetConfig> = {
  openPositions: {
    id: 'openPositions',
    labelKey: 'dashboard:widgets.openPositions',
    width: 1,
    height: 1,
  },
  totalPnLPercent: {
    id: 'totalPnLPercent',
    labelKey: 'dashboard:widgets.totalPnLPercent',
    width: 1,
    height: 1,
  },
  totalPnlOpen: {
    id: 'totalPnlOpen',
    labelKey: 'dashboard:widgets.totalPnlOpen',
    width: 1,
    height: 1,
  },
  totalRiskExposure: {
    id: 'totalRiskExposure',
    labelKey: 'dashboard:widgets.totalRiskExposure',
    width: 1,
    height: 1,
  },
  totalPnl: {
    id: 'totalPnl',
    labelKey: 'dashboard:widgets.totalPnl',
    width: 1,
    height: 1,
  },
  tradeList: {
    id: 'tradeList',
    labelKey: 'dashboard:widgets.tradeList',
    width: 4,
    height: 6,
  },
  totalTrades: {
    id: 'totalTrades',
    labelKey: 'dashboard:widgets.totalTrades',
    width: 1,
    height: 1,
  },
  winRate: {
    id: 'winRate',
    labelKey: 'dashboard:widgets.winRate',
    width: 1,
    height: 1,
  },
  profitFactor: {
    id: 'profitFactor',
    labelKey: 'dashboard:widgets.profitFactor',
    width: 1,
    height: 1,
  },
  avgPnlPercent: {
    id: 'avgPnlPercent',
    labelKey: 'dashboard:widgets.avgPnlPercent',
    width: 1,
    height: 1,
  },
  weightedPnlPercent: {
    id: 'weightedPnlPercent',
    labelKey: 'dashboard:widgets.weightedPnlPercent',
    width: 1,
    height: 1,
  },
  expectancy: {
    id: 'expectancy',
    labelKey: 'dashboard:widgets.expectancy',
    width: 1,
    height: 1,
  },
  averageWin: {
    id: 'averageWin',
    labelKey: 'dashboard:widgets.averageWin',
    width: 1,
    height: 1,
  },
  averageLoss: {
    id: 'averageLoss',
    labelKey: 'dashboard:widgets.averageLoss',
    width: 1,
    height: 1,
  },
  averageRR: {
    id: 'averageRR',
    labelKey: 'dashboard:widgets.averageRR',
    width: 1,
    height: 1,
  },
  feed: {
    id: 'feed',
    labelKey: 'dashboard:widgets.feed',
    width: 1,
    height: 8,
  },
}

export const DEFAULT_DASHBOARD_ORDER: DraggableWidgetId[] = [
  'openPositions',
  'totalPnLPercent',
  'totalRiskExposure',
  'tradeList',
]

export const DEFAULT_DASHBOARD_ENABLED: DraggableWidgetId[] = [
  'openPositions',
  'totalPnLPercent',
  'totalRiskExposure',
  'tradeList',
]

export function isFilterableWidget(id: DraggableWidgetId | string): boolean {
  const base = getBaseWidgetId(id)
  return (
    (FILTERABLE_METRIC_WIDGET_IDS as readonly string[]).includes(base) ||
    (FILTERABLE_OPEN_WIDGET_IDS as readonly string[]).includes(base)
  )
}

export function isOpenPositionFilterableWidget(id: string): id is FilterableOpenWidgetId {
  return (FILTERABLE_OPEN_WIDGET_IDS as readonly string[]).includes(getBaseWidgetId(id))
}

/** Extract base widget ID from instance ID (e.g. "winRate:abc" -> "winRate") */
export function getBaseWidgetId(id: string): string {
  const colon = id.indexOf(':')
  return colon >= 0 ? id.slice(0, colon) : id
}

/** Check if widget type allows multiple instances (all except tradeList) */
export function isMultiInstanceWidget(baseId: string): boolean {
  return baseId !== 'tradeList'
}

/** Generate unique instance ID for a widget */
export function createWidgetInstanceId(baseId: string): string {
  if (baseId === 'tradeList') return 'tradeList'
  return `${baseId}:${crypto.randomUUID?.()?.slice(0, 8) ?? Date.now().toString(36)}`
}

export type WidgetSettingsMap = Partial<Record<string, WidgetFilterSettings>>
